/**
 * tRPC Client Configuration
 *
 * @fileoverview Client-side tRPC setup for making API requests
 * @module lib/trpc/client
 * @see {@link src/lib/trpc/router.ts} - tRPC router definition
 * @see {@link src/lib/trpc/server.ts} - tRPC server setup
 * @see {@link https://trpc.io/docs/client/nextjs} - tRPC Next.js client docs
 */

import { createTRPCReact } from '@trpc/react-query';
import { httpBatchLink, httpLink, loggerLink } from '@trpc/client';
import { type AppRouter } from '@/lib/trpc/router';
import superjson from 'superjson'; // <-- Import superjson

/**
 * Create a tRPC client for use in React components
 * @see {@link https://trpc.io/docs/client/react} - tRPC React client docs
 */
export const trpc = createTRPCReact<AppRouter>();

/**
 * Get the base URL for API requests
 * @returns The base URL for API requests
 */
export function getBaseUrl() {
  if (typeof window !== 'undefined') {
    // Browser should use relative path
    return '';
  }
  
  // SSR should use the deployment URL or localhost
  if (process.env.NEXT_PUBLIC_APP_URL) {
    return process.env.NEXT_PUBLIC_APP_URL;
  }
  
  if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`;
  if (process.env.RENDER_INTERNAL_HOSTNAME) return `http://${process.env.RENDER_INTERNAL_HOSTNAME}:${process.env.PORT}`;
  
  return `http://localhost:${process.env.PORT ?? 3000}`;
}

/**
 * Create a tRPC client configuration
 * @returns tRPC client configuration with links
 */
export function createTRPCClient() {
  // Dynamic import for superjson - Removed as we import directly now
  // const transformer = 'superjson' as const; // Use string literal for type safety if possible

  return {
    // transformer, // <-- REMOVE this line
    links: [
      loggerLink({
        enabled: (opts) =>
          process.env.NODE_ENV === 'development' ||
          (opts.direction === 'down' && opts.result instanceof Error),
      }),
      httpBatchLink({
        url: `${getBaseUrl()}/api/trpc`,
        transformer: superjson, // <-- ADD transformer here
        headers() {
          return {};
        },
      }),
    ],
  };
}
